import React, { useCallback, useEffect, useMemo } from 'react';

import { InteractionManager, Pressable, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { format, isFuture, isPast } from 'date-fns';

import { EPGProgram } from 'custom_modules/react-native-epg';
import useIsLive from 'custom_modules/react-native-epg/Renderers/GridCellRenderer/useIsLive';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';

import { selectLayoutConfig } from 'store/config/selectors';

import setAccessibilityFocusTo from 'services/accessibility/setAccessibilityFocusTo';

import isFunction from 'utils/function';
import useProfileParentalRating from 'utils/hooks/useProfileParentalRating';
import needsToLockContent from 'utils/parentalControl/needsToLockContent';
import { isWeb, widthPercentageToDP } from 'utils/responsiveness';

import BaseTileImage from 'components/BaseTileImage';
import RecordingIcon from 'components/ContentMarkerIcons/RecordingMarker';
import { getRecordingStatusAccessibilityLabel } from 'components/ContentMarkerIcons/RecordingMarker/utils';
import Marker from 'components/Marker';
import { ImageContentType, ImageType } from 'components/NagraImage/types';
import ProgressBar from 'components/Ribbon/components/ProgressBar';
import { PLACEHOLDER_ICON_CENTER } from 'components/Ribbon/constants';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import { EpgProgramExtra, EpgProgramIcon } from '../../types';
import EpgLockedIcon from '../EpgLockedIcon';
import EpgReplayIcon from '../EpgReplayIcon';
import EpgUnsubscribedIcon from '../EpgUnsubscribedIcon';
import styles from './styles';

type Props = {
    program: EPGProgram<EpgProgramExtra>;
    wasChannelChanged?: boolean | null;
    onPress?(program: EPGProgram<EpgProgramExtra>): void;
};

const {
    CARDS_TITLE_CONTENT_LOCKED,
    ACCESSIBILITY: {
        EPG: { ACCESSIBILITY_EPG_START_END_TIME },
        DETAIL_PAGE: {
            ACCESSIBILITY_ICON_LOCKED,
            ACCESSIBILITY_ICON_CATCHUP,
            ACCESSIBILITY_ICON_SHOPPING_CART,
        },
    },
} = LANGUAGE_KEYS;

const EpgProgramCard = ({ program, onPress, wasChannelChanged }: Props) => {
    const {
        startTime,
        endTime,
        extra: {
            testID,
            iconTestID,
            time,
            subscribed,
            iconType,
            recordingItem,
            originalTitle,
        } = {},
    } = program;

    const { t } = useTranslation();
    const profileParentalControlRating = useProfileParentalRating();
    const { primary } = useSelector(selectLayoutConfig);

    const isLive = useIsLive(startTime, endTime);

    const programRef = React.useRef(null);

    const soIcon = useMemo(
        () =>
            (isLive || isFuture(startTime)) &&
            iconType === EpgProgramIcon.REPLAY_SO,
        [isLive, iconType, startTime],
    );
    const cuIcon = useMemo(
        () =>
            !isLive && isPast(endTime) && iconType === EpgProgramIcon.REPLAY_CU,
        [isLive, endTime, iconType],
    );
    const isContentLocked = useMemo(
        () =>
            needsToLockContent(
                profileParentalControlRating,
                program.extra?.rating,
            ),
        [program, profileParentalControlRating],
    );

    const renderTitle = useCallback(
        () => (
            <Text
                numberOfLines={1}
                ellipsizeMode="tail"
                category={ETextCategory.body2}
                color={ETextColor.neutral}
                style={styles.programCellText}
            >
                {isContentLocked
                    ? t(CARDS_TITLE_CONTENT_LOCKED)
                    : originalTitle}
            </Text>
        ),
        [isContentLocked, originalTitle, t],
    );

    const renderIcon = useCallback(() => {
        if (recordingItem) {
            return (
                <RecordingIcon
                    recordingItem={recordingItem}
                    collectionSuspensionActive={false}
                    size={20}
                    style={styles.recordingIcon}
                />
            );
        }

        if (isContentLocked) {
            return <EpgLockedIcon testID={iconTestID} />;
        }

        if (soIcon || cuIcon) {
            return <EpgReplayIcon testID={iconTestID} />;
        }

        if (iconType === EpgProgramIcon.NOT_SUBSCRIBED) {
            return <EpgUnsubscribedIcon testID={iconTestID} />;
        }

        return null;
    }, [isContentLocked, soIcon, cuIcon, iconType, recordingItem]);

    const accessibilityLabel = useMemo(() => {
        const programInfo = `${originalTitle}. ${format(
            startTime,
            'HH:mm',
        )} ${t(ACCESSIBILITY_EPG_START_END_TIME)} ${format(endTime, 'HH:mm')}`;

        if (recordingItem) {
            return `${programInfo}. ${t(
                getRecordingStatusAccessibilityLabel({
                    recordingItem,
                    collectionSuspensionActive: false,
                }),
            )}`;
        }

        if (isContentLocked) {
            return `${programInfo}. ${t(ACCESSIBILITY_ICON_LOCKED)}`;
        }

        if (soIcon || cuIcon) {
            return `${programInfo}. ${t(ACCESSIBILITY_ICON_CATCHUP)}`;
        }

        if (iconType === EpgProgramIcon.NOT_SUBSCRIBED) {
            return `${programInfo}. ${t(ACCESSIBILITY_ICON_SHOPPING_CART)}`;
        }

        return programInfo;
    }, [
        cuIcon,
        endTime,
        iconType,
        isContentLocked,
        originalTitle,
        recordingItem,
        soIcon,
        startTime,
        t,
    ]);

    const handleCardPress = useCallback(() => {
        if (isFunction(onPress)) {
            onPress(program);
        }
    }, [onPress, program]);

    const handleAccessibilityFocus = useCallback(() => {
        setTimeout(() => {
            setAccessibilityFocusTo(programRef);
        }, 1500);
    }, []);

    useEffect(() => {
        if (wasChannelChanged && isLive) {
            if (isWeb) {
                requestAnimationFrame(handleAccessibilityFocus);
            } else {
                InteractionManager.runAfterInteractions(
                    handleAccessibilityFocus,
                );
            }
        }
    }, [wasChannelChanged, isLive]);

    return (
        <Pressable
            testID={testID}
            accessibilityLabel={accessibilityLabel}
            accessibilityRole="button"
            onPress={handleCardPress}
            style={[styles.programCell, isLive && styles.programCellLive]}
            ref={programRef}
            {...(isWeb && { 'aria-live': 'assertive' })}
        >
            <View style={styles.programContainer}>
                <View style={styles.imageContainer}>
                    <BaseTileImage
                        contentId={program.id || ''}
                        contentType={ImageContentType.EPISODE}
                        imageType={ImageType.EPISODE}
                        width={128}
                        height={72}
                        style={gStyles.flex}
                        placeHolder={PLACEHOLDER_ICON_CENTER}
                        resizeMode="cover"
                    />
                    {isLive && (
                        <ProgressBar
                            start={startTime / 1000}
                            end={endTime / 1000}
                            isCollection={false}
                            backgroundColor={primary}
                            visibleItems={{ programCard: true }}
                            itemId="programCard"
                            barWidth={widthPercentageToDP(113)}
                        />
                    )}
                    {isLive && (
                        <Marker
                            startTime={startTime / 1000}
                            endTime={endTime / 1000}
                            bgColor={primary}
                            style={[styles.marker, styles.isOnLiveCard]}
                            textStyle={styles.markerText}
                        />
                    )}
                </View>
                <View style={styles.contentContainer}>
                    <View style={gStyles.flexRowSpace}>
                        <View style={styles.titleContainer}>
                            {renderTitle()}
                        </View>
                        {renderIcon()}
                    </View>

                    <Text
                        numberOfLines={1}
                        category={ETextCategory.caption2}
                        color={ETextColor.light_grey}
                        style={styles.programCellText}
                    >
                        {time}
                    </Text>
                </View>
            </View>
            {!subscribed && <View style={styles.unsubscribedOverlay} />}
        </Pressable>
    );
};

export default EpgProgramCard;
